import type { DbExecutor } from "../executor";
import type { CostEvidence, EstimateRequest, EvidenceProvider } from "../../estimating/evidence";

/**
 * TEMPLATE ASSUMPTION — the default the estimate already stands on.
 *
 * Always last in the ranking, and deliberately labeled an ASSUMPTION: it is
 * what a template says, not something this company has ever paid. Including
 * it as evidence is what makes the honesty visible — a line priced from a
 * template says so, instead of looking as authoritative as a receipt.
 *
 * Labor durations are converted with the same organization labor rate the
 * assembly engine uses, so the number shown here is the number in the
 * estimate.
 */
export class TemplateAssumptionProvider implements EvidenceProvider {
  readonly id = "template-assumption";
  readonly label = "Template assumption";
  readonly kind = "template-assumption" as const;

  constructor(private readonly db: DbExecutor) {}

  async availability() {
    return { available: true, note: null };
  }

  async gather(request: EstimateRequest): Promise<CostEvidence[]> {
    const { subject } = request;
    if (subject.kind !== "component" || subject.componentName === null) return [];

    const rows = await this.db.select<TemplateRow>(
      `SELECT tc.id, tc.name, tc.unit, tc.unit_cost_cents, tc.labor_minutes_x100,
              t.id AS template_id, t.display_name AS template_name
         FROM assembly_template_components tc
         JOIN assembly_templates t ON t.id = tc.template_id
         JOIN assembly_suggestions s ON s.template_id = t.id
        WHERE s.feature_definition_id = ?
          AND tc.name = ?
          AND tc.unit = ?
          AND t.archived_at IS NULL`,
      [subject.featureDefinitionId, subject.componentName, subject.unit],
    );
    if (rows.length === 0) return [];

    const rate = await this.laborRateCentsPerHour();
    const out: CostEvidence[] = [];
    for (const row of rows) {
      const minutes = row.labor_minutes_x100 === null ? null : Number(row.labor_minutes_x100);
      const explanation: string[] = [];
      let unitCostCentsX100: number;
      if (minutes !== null && minutes > 0) {
        // Same rule as the assembly engine: whole billable hours × rate.
        const hours = Math.ceil(minutes / 100 / 60);
        unitCostCentsX100 = hours * rate * 100;
        explanation.push(
          `${(minutes / 100).toFixed(0)} minutes per ${row.unit}, billed as ${hours} whole hour${hours === 1 ? "" : "s"} at the organization labor rate.`,
        );
        explanation.push("An estimating assumption — not a contractor quote.");
      } else {
        unitCostCentsX100 = Number(row.unit_cost_cents) * 100;
        explanation.push(`The "${row.template_name}" template assumes this cost per ${row.unit}.`);
        explanation.push("Nobody has paid this — it is a starting point until real evidence exists.");
      }
      out.push({
        id: `template:${row.id}`,
        providerId: this.id,
        providerLabel: `Template: ${row.template_name}`,
        kind: this.kind,
        unitCostCentsX100,
        unit: row.unit,
        lowCentsX100: null,
        highCentsX100: null,
        sampleSize: 0,
        asOf: null,
        market: null,
        explanation,
        observations: [
          {
            label: `${row.template_name} — ${row.name}`,
            unitCostCentsX100,
            quantityX100: null,
            date: "",
            projectJobNumber: null,
            projectId: null,
            market: null,
            ref: { kind: "template", id: row.template_id },
          },
        ],
        quality: "indicative",
      });
    }
    return out.slice(0, 3);
  }

  private async laborRateCentsPerHour(): Promise<number> {
    const rows = await this.db.select<{ value: string }>(
      "SELECT value FROM app_settings WHERE key = 'labor_rate_cents_per_hour'",
    );
    const parsed = Number(rows[0]?.value ?? "");
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 2500;
  }
}

interface TemplateRow {
  id: string;
  name: string;
  unit: string;
  unit_cost_cents: number;
  labor_minutes_x100: number | null;
  template_id: string;
  template_name: string;
}
